import { useState, useCallback, useRef, useMemo, useId, forwardRef, useImperativeHandle } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import Group from 'components/utils/Group'
import { normaliseMessage } from 'tripPlanner/api/chatUtils'
import { useVoiceComposer } from 'tripPlanner/components/Chat/hooks/useVoiceComposer'
import { useSettleComposerFocus } from 'tripPlanner/components/Chat/hooks/useSettleComposerFocus'
import RecordingOverlay from 'tripPlanner/components/Chat/RecordingOverlay'
import ComposerMicrophoneButton from 'tripPlanner/components/Chat/ComposerMicrophoneButton'
import { appendTranscript } from 'tripPlanner/components/Chat/utils/transcript'
import BorderlessTextAreaOnly from 'components/Common/Form/Input/BorderlessTextAreaOnly'
import ChatInputShell from 'tripPlanner/components/Chat/ChatInputShell'
import MorphingSendButton from 'tripPlanner/components/Chat/MorphingSendButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'

export interface DrawerAskAIInputHandle {
  focus: () => void
}

const InputSizer = styled.div`
  width: 60%;
  max-width: min(100%, ${rem(600)});
  min-width: 0;
  box-sizing: border-box;
  transition: width 0.25s ease, max-width 0.25s ease;

  &.active {
    width: 100%;
    max-width: min(100%, ${rem(652)});
  }

  &.force-expanded {
    width: 100%;
    max-width: min(100%, ${rem(652)});
  }

  ${mediaQueryUp.tablet} {
    &.force-expanded:not(.active) {
      width: 60%;
      max-width: min(100%, ${rem(600)});
    }
  }
`

const TextAreaWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  width: 100%;

  > * {
    width: 100%;
    min-width: 0;
  }

  textarea {
    &:placeholder-shown {
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`

interface Props {
  onSubmit: (message: string) => void | Promise<void>
  disabled?: boolean
  placeholder?: string
  hasActiveSession?: boolean
  forceExpanded?: boolean
}

const DrawerAskAIInput = forwardRef<DrawerAskAIInputHandle, Props>(function DrawerAskAIInput({
  onSubmit,
  disabled = false,
  placeholder: placeholderProp,
  hasActiveSession = false,
  forceExpanded = false,
}, ref) {
  const messageFieldId = useId()
  const intl = useIntl()
  const placeholder = placeholderProp ?? intl.formatMessage({
    id: '8v8W1PlItxkd_1u8oc57',
    defaultMessage: 'Ask about this...',
    description: 'Default placeholder text for AI chat input in trip planner drawer',
  })
  const [message, setMessage] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  // Device, not viewport: a narrow desktop window must keep Enter-to-send.
  const hasSoftKeyboard = useIsMobileOrTablet()
  const hasMessage = Boolean(message.trim())
  const isActive = isFocused || hasMessage || hasActiveSession
  const settleFocus = useSettleComposerFocus(textareaRef)

  useImperativeHandle(ref, () => ({
    focus: () => {
      textareaRef.current?.focus()
    },
  }), [])

  const handleTranscript = useCallback((transcript: string) => {
    setMessage(currentMessage => appendTranscript(currentMessage, transcript))
  }, [])

  const {
    isRecording,
    isTranscribing,
    waveformLevels,
    filledBars,
    startRecording,
    cancelRecording,
    submitRecording,
  } = useVoiceComposer({ onTranscript: handleTranscript, settleFocus })

  const handleFocus = useCallback(() => {
    setIsFocused(true)
  }, [])

  const handleSubmit = useCallback(() => {
    if (disabled || !hasMessage) return
    const processed = normaliseMessage(message)
    onSubmit(processed)
    setMessage('')
    settleFocus()
  }, [message, onSubmit, disabled, hasMessage, settleFocus])

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // On a soft keyboard the return key composes a new line; the send button submits.
    if (event.key === 'Enter' && !event.shiftKey && !hasSoftKeyboard) {
      event.preventDefault()
      handleSubmit()
    }
  }, [handleSubmit, hasSoftKeyboard])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.target.value)
  }, [])

  const handleBlur = useCallback(() => {
    setIsFocused(false)
  }, [])

  const shouldShowOverlay = isRecording || isTranscribing

  const recordingOverlay = useMemo(() => {
    if (!shouldShowOverlay) return null

    return (
      <RecordingOverlay
        waveformLevels={waveformLevels}
        filledBars={filledBars}
        onCancel={cancelRecording}
        onSubmit={submitRecording}
        isTranscribing={isTranscribing}
      />
    )
  }, [
    cancelRecording,
    filledBars,
    isTranscribing,
    shouldShowOverlay,
    submitRecording,
    waveformLevels,
  ])

  return (
    <InputSizer className={clsx({ active: isActive, 'force-expanded': forceExpanded })}>
      <ChatInputShell compact={!isActive}>
        <Group direction="horizontal" gap={8} verticalAlign="center" fullWidth>
          <TextAreaWrapper>
            <BorderlessTextAreaOnly
              ref={textareaRef}
              id={messageFieldId}
              name="tripPlannerDrawerAskAiMessage"
              placeholder={placeholder}
              value={message}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              enterKeyHint={hasSoftKeyboard ? 'enter' : undefined}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          </TextAreaWrapper>
          <ComposerMicrophoneButton onClick={startRecording} disabled={disabled} />
          <MorphingSendButton
            expanded={hasMessage}
            onClick={handleSubmit}
            disabled={disabled || !hasMessage}
          />
          {recordingOverlay}
        </Group>
      </ChatInputShell>
    </InputSizer>
  )
})

DrawerAskAIInput.displayName = 'DrawerAskAIInput'

export default DrawerAskAIInput
